import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { useEmployeeSession } from '../EmployeeSessionProvider';
import { subscribePosStream } from '../../services/pos-stream/pos-stream.service';
import { hardRefresh } from '../../utils/hard-refresh';
import { REFRESH_CROSS_TAB_PULSE_KEY } from '../../pages/PosShellPage/hooks/refresh.toasts';
import { useAppVersionWatch } from '../../hooks/useAppVersionWatch';
import { markVersionPrompted } from '../../utils/app-version';

const tenantIdFromPathname = (pathname: string): string | null => {
  const match = pathname.match(/^\/t\/([^/]+)\/(?:pos|kds|dashboard|backoffice|maintenance)(?:\/|$)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const isPosOrKdsPath = (pathname: string): boolean =>
  /^\/t\/[^/]+\/(?:pos|kds)(?:\/|$)/.test(pathname);

// The POS shell owns its own version watch + defer-until-safe reload; the provider covers the rest.
const isPosShellPath = (pathname: string): boolean => /^\/t\/[^/]+\/pos(?:\/|$)/.test(pathname);

export const TenantMaintenanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshAvailableTenants } = useAuth();
  const { clearEmployeeState, hydrateEmployeeState } = useEmployeeSession();
  const tenantId = useMemo(() => tenantIdFromPathname(location.pathname), [location.pathname]);
  const [employeeSession, setEmployeeSession] = useState<string | null>(null);
  const reconcileInFlightRef = useRef(false);
  const MAINTENANCE_POLL_MS = 5000;

  // Nothing to defer off the POS: mark the build applied and reload right away.
  useAppVersionWatch(!isPosShellPath(location.pathname), (version) => {
    markVersionPrompted(version);
    void hardRefresh();
  });

  const syncEmployeeSession = useCallback((): string | null => {
    if (!tenantId) {
      setEmployeeSession(null);
      return null;
    }
    const next = hydrateEmployeeState(tenantId)?.employeeSession ?? null;
    setEmployeeSession(next);
    return next;
  }, [hydrateEmployeeState, tenantId]);

  useEffect(() => {
    void location.pathname;
    syncEmployeeSession();
  }, [location.pathname, syncEmployeeSession]);

  const reconcileMaintenanceMode = useCallback(async () => {
    if (!tenantId || user?.role === 'super_admin' || reconcileInFlightRef.current) {
      return;
    }

    reconcileInFlightRef.current = true;
    try {
      const tenants = await refreshAvailableTenants();
      const maintenanceMode = tenants.some((tenant) => tenant.tenantId === tenantId && tenant.maintenanceMode);
      const maintenancePath = `/t/${encodeURIComponent(tenantId)}/maintenance`;
      if (!maintenanceMode && location.pathname === maintenancePath) {
        navigate(`/t/${encodeURIComponent(tenantId)}/pos`, { replace: true });
      }
      if (maintenanceMode && location.pathname !== maintenancePath) {
        navigate(maintenancePath, { replace: true });
      }
    } catch {
      // Keep the current screen if the lightweight state check is temporarily unavailable.
    } finally {
      reconcileInFlightRef.current = false;
    }
  }, [location.pathname, navigate, refreshAvailableTenants, tenantId, user?.role]);

  useEffect(() => {
    if (!tenantId || user?.role === 'super_admin') {
      return;
    }

    // A suspended background tab can miss the maintenance SSE event. Reconcile immediately on
    // return; while on the maintenance screen, poll lightly so it can always release itself.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncEmployeeSession();
        void reconcileMaintenanceMode();
      }
    };
    window.addEventListener('pageshow', onVisibilityChange);
    window.addEventListener('focus', onVisibilityChange);
    document.addEventListener('visibilitychange', onVisibilityChange);
    void reconcileMaintenanceMode();

    const maintenancePath = `/t/${encodeURIComponent(tenantId)}/maintenance`;
    const pollId = location.pathname === maintenancePath
      ? window.setInterval(() => {
        syncEmployeeSession();
        void reconcileMaintenanceMode();
      }, MAINTENANCE_POLL_MS)
      : null;

    return () => {
      window.removeEventListener('pageshow', onVisibilityChange);
      window.removeEventListener('focus', onVisibilityChange);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (pollId !== null) {
        window.clearInterval(pollId);
      }
    };
  }, [location.pathname, reconcileMaintenanceMode, syncEmployeeSession, tenantId, user?.role]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== REFRESH_CROSS_TAB_PULSE_KEY || !event.newValue) {
        return;
      }
      try {
        const payload = JSON.parse(event.newValue) as { tenantId?: string | null };
        const pulseTenantId = typeof payload?.tenantId === 'string' ? payload.tenantId : null;
        if (tenantId && pulseTenantId && tenantId !== pulseTenantId) {
          return;
        }
      } catch {
        // Parse failure: still honor the pulse as a best effort.
      }
      void hardRefresh();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || user?.role === 'super_admin') {
      return;
    }

    const maintenancePath = `/t/${encodeURIComponent(tenantId)}/maintenance`;
    return subscribePosStream(
      tenantId,
      employeeSession,
      (event) => {
        if (event.type === 'pos.refresh-requested') {
          // POS/KDS own the safe-zone gate while an employee session is active.
          // The employee selection gate is under /pos without an employee session, so it
          // should refresh from the provider like other non-POS pages.
          const isPosOrKds = isPosOrKdsPath(location.pathname);
          if (!isPosOrKds || !employeeSession) {
            if (event.reason === 'session-closed' && tenantId) {
              clearEmployeeState(tenantId);
            }
            void hardRefresh();
          }
          return;
        }

        if (event.type !== 'tenant.maintenance-mode-changed') {
          return;
        }

        if (event.maintenanceMode && location.pathname !== maintenancePath) {
          navigate(maintenancePath, { replace: true });
        }
        if (!event.maintenanceMode && location.pathname === maintenancePath) {
          navigate(`/t/${encodeURIComponent(tenantId)}/pos`, { replace: true });
        }
      },
      undefined,
      undefined,
      () => {
        void reconcileMaintenanceMode();
      },
      () => {
        void reconcileMaintenanceMode();
      },
    );
  }, [clearEmployeeState, employeeSession, location.pathname, navigate, reconcileMaintenanceMode, tenantId, user?.role]);

  return <>{children}</>;
};
